/* GOPSHOP G//VAULT — isolated Canvas game v1.0.0 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-gvg-root]';
  var STORAGE_KEY = 'gopshop-gvault-best-v1';
  var instanceCount = 0;

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function pad(value, length) {
    return String(Math.max(0, Math.floor(value))).padStart(length || 3, '0');
  }

  function safeNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function roundedRectPath(ctx, x, y, width, height, radius) {
    var r = Math.min(radius, Math.abs(width) / 2, Math.abs(height) / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + width, y, x + width, y + height, r);
    ctx.arcTo(x + width, y + height, x, y + height, r);
    ctx.arcTo(x, y + height, x, y, r);
    ctx.arcTo(x, y, x + width, y, r);
    ctx.closePath();
  }

  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).catch(function () {
        return legacyCopy(text);
      });
    }
    return legacyCopy(text);
  }

  function legacyCopy(text) {
    return new Promise(function (resolve, reject) {
      var input = document.createElement('textarea');
      input.value = text;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.top = '-9999px';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.focus();
      input.select();
      try {
        if (document.execCommand('copy')) resolve();
        else reject(new Error('Copy command failed'));
      } catch (error) {
        reject(error);
      } finally {
        input.remove();
      }
    });
  }

  function VaultAudio() {
    this.context = null;
    this.master = null;
    this.musicTimer = 0;
    this.step = 0;
    this.enabled = true;
  }

  VaultAudio.prototype.unlock = function () {
    var AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return Promise.resolve(false);

    if (!this.context) {
      this.context = new AudioContextClass();
      this.master = this.context.createGain();
      this.master.gain.value = this.enabled ? 0.16 : 0;
      this.master.connect(this.context.destination);
    }

    if (this.context.state === 'suspended') {
      return this.context.resume().then(function () { return true; });
    }

    return Promise.resolve(true);
  };

  VaultAudio.prototype.setEnabled = function (enabled) {
    this.enabled = Boolean(enabled);
    if (this.master && this.context) {
      this.master.gain.cancelScheduledValues(this.context.currentTime);
      this.master.gain.setTargetAtTime(this.enabled ? 0.16 : 0, this.context.currentTime, 0.025);
    }
    if (!this.enabled) this.stopMusic();
  };

  VaultAudio.prototype.tone = function (frequency, duration, type, volume, detune, delay) {
    if (!this.enabled || !this.context || !this.master) return;
    var now = this.context.currentTime + (delay || 0);
    var oscillator = this.context.createOscillator();
    var gain = this.context.createGain();
    oscillator.type = type || 'sine';
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.detune.setValueAtTime(detune || 0, now);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume || 0.08), now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.03);
  };

  VaultAudio.prototype.noise = function (duration, volume, highpass, delay) {
    if (!this.enabled || !this.context || !this.master) return;
    var sampleRate = this.context.sampleRate;
    var buffer = this.context.createBuffer(1, Math.max(1, sampleRate * duration), sampleRate);
    var data = buffer.getChannelData(0);
    for (var i = 0; i < data.length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    }

    var source = this.context.createBufferSource();
    var filter = this.context.createBiquadFilter();
    var gain = this.context.createGain();
    var now = this.context.currentTime + (delay || 0);
    source.buffer = buffer;
    filter.type = 'highpass';
    filter.frequency.value = highpass || 1600;
    gain.gain.setValueAtTime(Math.max(0.0001, volume || 0.025), now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    source.start(now);
  };

  VaultAudio.prototype.kick = function () {
    if (!this.enabled || !this.context || !this.master) return;
    var now = this.context.currentTime;
    var oscillator = this.context.createOscillator();
    var gain = this.context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(116, now);
    oscillator.frequency.exponentialRampToValueAtTime(46, now + 0.13);
    gain.gain.setValueAtTime(0.11, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
    oscillator.connect(gain);
    gain.connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + 0.17);
  };

  VaultAudio.prototype.musicBeat = function () {
    if (!this.enabled || !this.context) return;
    var step = this.step % 16;
    if (step === 0 || step === 6 || step === 10) this.kick();
    if (step === 3 || step === 7 || step === 11 || step === 15) {
      this.tone(step === 7 ? 330 : 280, 0.055, 'triangle', 0.025, -500);
    }
    if (step % 2 === 1) this.noise(0.035, 0.012, 2900);
    if (step === 0 || step === 8) {
      this.tone(step === 0 ? 73.42 : 82.41, 0.75, 'sine', 0.018, 0);
    }
    if (step === 4 || step === 12) {
      this.tone(step === 4 ? 146.83 : 164.81, 0.16, 'sawtooth', 0.01, -1200);
    }
    this.step += 1;
  };

  VaultAudio.prototype.startMusic = function () {
    this.stopMusic();
    if (!this.enabled || !this.context) return;
    this.step = 0;
    this.musicBeat();
    var self = this;
    this.musicTimer = window.setInterval(function () {
      self.musicBeat();
    }, 215);
  };

  VaultAudio.prototype.stopMusic = function () {
    if (this.musicTimer) window.clearInterval(this.musicTimer);
    this.musicTimer = 0;
  };

  VaultAudio.prototype.pickup = function (type) {
    if (type === 'core') {
      this.tone(392, 0.28, 'sine', 0.08, 0, 0);
      this.tone(587.33, 0.34, 'triangle', 0.06, 0, 0.045);
      this.tone(783.99, 0.42, 'sine', 0.045, 0, 0.09);
    } else if (type === 'shard') {
      this.tone(440, 0.16, 'triangle', 0.055);
      this.tone(659.25, 0.2, 'sine', 0.04, 0, 0.035);
    } else {
      this.tone(523.25, 0.13, 'sine', 0.04);
    }
  };

  VaultAudio.prototype.turn = function () {
    this.tone(190, 0.03, 'triangle', 0.008);
  };

  VaultAudio.prototype.crash = function () {
    this.noise(0.3, 0.09, 260);
    this.tone(110, 0.45, 'sawtooth', 0.055, -900);
  };

  VaultAudio.prototype.win = function () {
    this.stopMusic();
    var notes = [261.63, 329.63, 392, 523.25, 659.25];
    for (var i = 0; i < notes.length; i += 1) {
      this.tone(notes[i], 0.55, i % 2 ? 'triangle' : 'sine', 0.055, 0, i * 0.11);
    }
  };

  VaultAudio.prototype.destroy = function () {
    this.stopMusic();
    if (this.context && this.context.state !== 'closed') this.context.close();
    this.context = null;
    this.master = null;
  };

  function GopVault(root) {
    this.root = root;
    this.id = root.dataset.sectionId || String(++instanceCount);
    this.targetScore = clamp(safeNumber(root.dataset.targetScore, 30), 10, 100);
    this.discountCode = root.dataset.discountCode || 'GVAULT15';
    this.rewardValue = root.dataset.rewardValue || '15 %';
    this.minimumOrder = root.dataset.minimumOrder || '100 €';
    this.copiedLabel = root.dataset.copiedLabel || 'CODE COPIED';
    this.pauseLabel = root.dataset.pauseLabel || 'Pause game';
    this.resumeLabel = root.dataset.resumeLabel || 'Resume game';
    this.soundEnabled = root.dataset.soundDefault !== 'false';
    this.reducedMotion = Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    this.modal = root.querySelector('[data-gvg-modal]');
    this.shell = root.querySelector('[data-gvg-shell]');
    this.startScreen = root.querySelector('[data-gvg-start-screen]');
    this.gameScreen = root.querySelector('[data-gvg-game]');
    this.winScreen = root.querySelector('[data-gvg-win]');
    this.lossScreen = root.querySelector('[data-gvg-loss]');
    this.pauseScreen = root.querySelector('[data-gvg-pause-screen]');
    this.canvas = root.querySelector('[data-gvg-canvas]');
    this.boardWrap = root.querySelector('[data-gvg-board-wrap]');
    this.hud = root.querySelector('.gvg__hud');
    this.gameFooter = root.querySelector('.gvg__game-footer');
    this.context = this.canvas ? this.canvas.getContext('2d', { alpha: false }) : null;
    this.scoreElement = root.querySelector('[data-gvg-score]');
    this.scoreMiniElement = root.querySelector('[data-gvg-score-mini]');
    this.levelElement = root.querySelector('[data-gvg-level]');
    this.progressElement = root.querySelector('[data-gvg-progress]');
    this.finalScoreElement = root.querySelector('[data-gvg-final-score]');
    this.bestScoreElement = root.querySelector('[data-gvg-best-score]');
    this.soundButton = root.querySelector('[data-gvg-sound]');
    this.pauseButton = root.querySelector('[data-gvg-pause]');
    this.liveRegion = root.querySelector('[data-gvg-live]');

    this.audio = new VaultAudio();
    this.audio.setEnabled(this.soundEnabled);

    this.cols = 18;
    this.rows = 24;
    this.state = 'idle';
    this.modalOpen = false;
    this.paused = false;
    this.score = 0;
    this.level = 1;
    this.speed = 182;
    this.snake = [];
    this.previousSnake = [];
    this.direction = { x: 0, y: -1 };
    this.pendingDirection = { x: 0, y: -1 };
    this.pickupItem = null;
    this.particles = [];
    this.ambient = [];
    this.shake = 0;
    this.lastFrame = 0;
    this.accumulator = 0;
    this.frameRequest = 0;
    this.canvasWidth = 0;
    this.canvasHeight = 0;
    this.cell = 0;
    this.offsetX = 0;
    this.offsetY = 0;
    this.touchStart = null;
    this.lastFocused = null;
    this.markImage = null;
    this.bestScore = this.readBestScore();
    this.destroyed = false;
    this.modalParent = this.modal ? this.modal.parentNode : null;
    this.modalNextSibling = this.modal ? this.modal.nextSibling : null;

    this.boundFrame = this.frame.bind(this);
    this.boundResize = this.resizeCanvas.bind(this);
    this.boundKeydown = this.onKeydown.bind(this);
    this.boundVisibility = this.onVisibilityChange.bind(this);

    this.createAmbient();
    this.loadMark();
    this.bindEvents();
    this.syncSoundButton();
    this.updateHud();
    this.setupResizeObserver();
    root.dataset.gvgReady = 'true';
  }

  GopVault.prototype.queryAll = function (selector) {
    return Array.prototype.slice.call(this.root.querySelectorAll(selector));
  };

  GopVault.prototype.bindEvents = function () {
    var self = this;

    this.queryAll('[data-gvg-open]').forEach(function (button) {
      button.addEventListener('click', function () { self.open(); });
    });

    this.queryAll('[data-gvg-close]').forEach(function (button) {
      button.addEventListener('click', function () { self.close(); });
    });

    this.queryAll('[data-gvg-start]').forEach(function (button) {
      button.addEventListener('click', function () { self.startGame(); });
    });

    this.queryAll('[data-gvg-retry]').forEach(function (button) {
      button.addEventListener('click', function () { self.startGame(); });
    });

    this.queryAll('[data-gvg-resume]').forEach(function (button) {
      button.addEventListener('click', function () { self.togglePause(false); });
    });

    this.queryAll('[data-gvg-direction]').forEach(function (button) {
      var direction = button.dataset.gvgDirection;
      var activate = function (event) {
        event.preventDefault();
        self.setDirectionByName(direction);
        button.classList.add('is-active');
        window.setTimeout(function () { button.classList.remove('is-active'); }, 120);
      };
      button.addEventListener('pointerdown', activate, { passive: false });
    });

    this.queryAll('[data-gvg-copy]').forEach(function (button) {
      button.addEventListener('click', function () { self.copyReward(button); });
    });

    if (this.soundButton) {
      this.soundButton.addEventListener('click', function () { self.toggleSound(); });
    }

    if (this.pauseButton) {
      this.pauseButton.addEventListener('click', function () { self.togglePause(); });
    }

    if (this.canvas) {
      this.canvas.addEventListener('pointerdown', function (event) {
        self.touchStart = { x: event.clientX, y: event.clientY };
      }, { passive: true });

      this.canvas.addEventListener('pointerup', function (event) {
        if (!self.touchStart) return;
        var dx = event.clientX - self.touchStart.x;
        var dy = event.clientY - self.touchStart.y;
        self.touchStart = null;
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 18) return;
        if (Math.abs(dx) > Math.abs(dy)) self.setDirectionByName(dx > 0 ? 'right' : 'left');
        else self.setDirectionByName(dy > 0 ? 'down' : 'up');
      }, { passive: true });

      this.canvas.addEventListener('pointercancel', function () { self.touchStart = null; }, { passive: true });
    }

    document.addEventListener('keydown', this.boundKeydown);
    document.addEventListener('visibilitychange', this.boundVisibility);
  };

  GopVault.prototype.setupResizeObserver = function () {
    if (window.ResizeObserver && this.shell) {
      this.resizeObserver = new ResizeObserver(this.boundResize);
      this.resizeObserver.observe(this.shell);
    } else {
      window.addEventListener('resize', this.boundResize, { passive: true });
    }
  };

  GopVault.prototype.loadMark = function () {
    var url = this.root.dataset.gMarkUrl;
    if (!url) return;
    var image = new Image();
    var self = this;
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    image.onload = function () { self.markImage = image; };
    image.onerror = function () { self.markImage = null; };
    image.src = url;
  };

  GopVault.prototype.createAmbient = function () {
    this.ambient = [];
    for (var i = 0; i < 48; i += 1) {
      this.ambient.push({
        x: Math.random(),
        y: Math.random(),
        size: 0.4 + Math.random() * 1.2,
        alpha: 0.08 + Math.random() * 0.25,
        phase: Math.random() * Math.PI * 2,
        drift: 0.4 + Math.random() * 0.8
      });
    }
  };

  GopVault.prototype.readBestScore = function () {
    try {
      return clamp(safeNumber(window.localStorage.getItem(STORAGE_KEY), 0), 0, 9999);
    } catch (error) {
      return 0;
    }
  };

  GopVault.prototype.writeBestScore = function () {
    if (this.score <= this.bestScore) return;
    this.bestScore = this.score;
    try {
      window.localStorage.setItem(STORAGE_KEY, String(this.bestScore));
    } catch (error) {
      /* Storage may be unavailable in private browsing. */
    }
  };

  GopVault.prototype.open = function () {
    if (!this.modal || this.modalOpen) return;
    this.modalOpen = true;
    this.lastFocused = document.activeElement;
    this.mountModal();
    this.modal.hidden = false;
    this.modal.classList.remove('is-closing');
    this.modal.classList.add('is-opening');
    document.body.classList.add('gvg-no-scroll');
    this.showScreen('start');
    this.state = 'idle';
    this.syncSoundButton();

    var self = this;
    window.requestAnimationFrame(function () {
      self.resizeCanvas();
      if (self.shell) self.shell.focus({ preventScroll: true });
    });
    window.setTimeout(function () {
      if (self.modal) self.modal.classList.remove('is-opening');
    }, 620);
  };

  GopVault.prototype.close = function () {
    if (!this.modal || !this.modalOpen) return;
    this.modalOpen = false;
    this.stopLoop();
    this.audio.stopMusic();
    this.state = 'idle';
    this.paused = false;
    this.modal.classList.remove('is-opening');
    this.modal.classList.add('is-closing');
    document.body.classList.remove('gvg-no-scroll');

    var self = this;
    window.setTimeout(function () {
      if (!self.modal) return;
      self.modal.hidden = true;
      self.modal.classList.remove('is-closing');
      self.showScreen('start');
      self.restoreModal();
      if (self.lastFocused && typeof self.lastFocused.focus === 'function') {
        self.lastFocused.focus({ preventScroll: true });
      }
    }, 270);
  };

  GopVault.prototype.showScreen = function (name) {
    if (this.startScreen) this.startScreen.hidden = name !== 'start';
    if (this.gameScreen) this.gameScreen.hidden = name !== 'game';
    if (this.winScreen) this.winScreen.hidden = name !== 'win';
    if (this.lossScreen) this.lossScreen.hidden = name !== 'loss';
    if (this.pauseScreen) this.pauseScreen.hidden = true;
  };

  GopVault.prototype.mountModal = function () {
    if (!this.modal || this.modal.parentNode === document.body) return;
    var styles = window.getComputedStyle(this.root);
    ['--gvg-mint', '--gvg-ice', '--gvg-ink', '--gvg-radius', '--gvg-white', '--gvg-muted', '--gvg-line', '--gvg-ease'].forEach(function (variable) {
      var value = styles.getPropertyValue(variable).trim();
      if (value) this.modal.style.setProperty(variable, value);
    }, this);
    if (styles.fontFamily) this.modal.style.fontFamily = styles.fontFamily;
    document.body.appendChild(this.modal);
  };

  GopVault.prototype.restoreModal = function () {
    if (!this.modal || !this.modalParent || this.modal.parentNode === this.modalParent) return;
    if (this.modalNextSibling && this.modalNextSibling.parentNode === this.modalParent) {
      this.modalParent.insertBefore(this.modal, this.modalNextSibling);
    } else {
      this.modalParent.appendChild(this.modal);
    }
  };

  GopVault.prototype.startGame = function () {
    if (!this.modalOpen || !this.context) return;
    this.showScreen('game');
    this.state = 'playing';
    this.paused = false;
    this.score = 0;
    this.level = 1;
    this.speed = 182;
    this.accumulator = 0;
    this.lastFrame = performance.now();
    this.particles = [];
    this.shake = 0;
    this.direction = { x: 0, y: -1 };
    this.pendingDirection = { x: 0, y: -1 };

    var centerX = Math.floor(this.cols / 2);
    var startY = Math.floor(this.rows * 0.7);
    this.snake = [
      { x: centerX, y: startY },
      { x: centerX, y: startY + 1 },
      { x: centerX, y: startY + 2 },
      { x: centerX, y: startY + 3 },
      { x: centerX, y: startY + 4 }
    ];
    this.previousSnake = this.snake.map(function (part) { return { x: part.x, y: part.y }; });
    this.spawnPickup();
    this.updateHud();
    this.syncPauseButton();
    this.resizeCanvas();

    var self = this;
    this.audio.setEnabled(this.soundEnabled);
    this.audio.unlock().then(function () {
      if (self.state === 'playing' && self.soundEnabled) self.audio.startMusic();
    });

    this.stopLoop();
    this.frameRequest = window.requestAnimationFrame(this.boundFrame);
  };

  GopVault.prototype.stopLoop = function () {
    if (this.frameRequest) window.cancelAnimationFrame(this.frameRequest);
    this.frameRequest = 0;
  };

  GopVault.prototype.frame = function (timestamp) {
    if (this.destroyed || this.state !== 'playing' || !this.modalOpen) {
      this.frameRequest = 0;
      return;
    }

    var delta = clamp(timestamp - this.lastFrame, 0, 80);
    this.lastFrame = timestamp;

    if (!this.paused) {
      this.accumulator += delta;
      while (this.accumulator >= this.speed && this.state === 'playing') {
        this.accumulator -= this.speed;
        this.tick();
      }
      this.updateParticles(delta / 1000);
    }

    this.render(timestamp, this.paused ? 1 : clamp(this.accumulator / this.speed, 0, 1));
    this.frameRequest = window.requestAnimationFrame(this.boundFrame);
  };

  GopVault.prototype.tick = function () {
    if (!this.snake.length) return;
    this.previousSnake = this.snake.map(function (part) { return { x: part.x, y: part.y }; });
    this.direction = { x: this.pendingDirection.x, y: this.pendingDirection.y };

    var head = this.snake[0];
    var nextHead = { x: head.x + this.direction.x, y: head.y + this.direction.y };
    var willEat = this.pickupItem && nextHead.x === this.pickupItem.x && nextHead.y === this.pickupItem.y;
    var bodyToCheck = willEat ? this.snake : this.snake.slice(0, -1);
    var hitWall = nextHead.x < 0 || nextHead.y < 0 || nextHead.x >= this.cols || nextHead.y >= this.rows;
    var hitSelf = bodyToCheck.some(function (part) {
      return part.x === nextHead.x && part.y === nextHead.y;
    });

    if (hitWall || hitSelf) {
      this.lose();
      return;
    }

    this.snake.unshift(nextHead);
    if (willEat) {
      this.collectPickup();
    } else {
      this.snake.pop();
    }
  };

  GopVault.prototype.collectPickup = function () {
    var pickup = this.pickupItem;
    if (!pickup) return;

    this.score += pickup.value;
    this.writeBestScore();
    this.audio.pickup(pickup.type);
    this.burst(pickup.x, pickup.y, pickup.type);
    this.shake = pickup.type === 'core' ? 7 : pickup.type === 'shard' ? 4 : 2;

    if (navigator.vibrate) {
      navigator.vibrate(pickup.type === 'core' ? [18, 22, 30] : pickup.type === 'shard' ? 18 : 10);
    }

    this.updateDifficulty();
    this.updateHud();

    if (this.score >= this.targetScore) {
      this.win();
      return;
    }

    this.spawnPickup();
  };

  GopVault.prototype.updateDifficulty = function () {
    var firstThreshold = Math.ceil(this.targetScore / 3);
    var secondThreshold = Math.ceil((this.targetScore * 2) / 3);
    var newLevel = this.score >= secondThreshold ? 3 : this.score >= firstThreshold ? 2 : 1;
    if (newLevel !== this.level) {
      this.level = newLevel;
      this.speed = newLevel === 3 ? 108 : newLevel === 2 ? 142 : 182;
      this.burst(this.snake[0].x, this.snake[0].y, 'level');
      this.audio.tone(newLevel === 3 ? 659.25 : 523.25, 0.32, 'triangle', 0.06);
    }
  };

  GopVault.prototype.spawnPickup = function () {
    var available = [];
    for (var y = 0; y < this.rows; y += 1) {
      for (var x = 0; x < this.cols; x += 1) {
        var occupied = this.snake.some(function (part) { return part.x === x && part.y === y; });
        if (!occupied) available.push({ x: x, y: y });
      }
    }

    if (!available.length) {
      this.win();
      return;
    }

    var position = available[Math.floor(Math.random() * available.length)];
    var roll = Math.random();
    var type = roll < 0.09 ? 'core' : roll < 0.33 ? 'shard' : 'orb';
    var value = type === 'core' ? 3 : type === 'shard' ? 2 : 1;
    this.pickupItem = { x: position.x, y: position.y, type: type, value: value, born: performance.now() };
  };

  GopVault.prototype.burst = function (gridX, gridY, type) {
    var center = this.gridToCanvas(gridX, gridY);
    var amount = this.reducedMotion ? 5 : type === 'core' ? 30 : type === 'shard' ? 18 : type === 'level' ? 22 : 12;
    var mint = this.getColor('--gvg-mint', '#79ffa7');
    var ice = this.getColor('--gvg-ice', '#bdf7ff');
    for (var i = 0; i < amount; i += 1) {
      var angle = Math.random() * Math.PI * 2;
      var force = 25 + Math.random() * (type === 'core' ? 120 : 75);
      this.particles.push({
        x: center.x,
        y: center.y,
        vx: Math.cos(angle) * force,
        vy: Math.sin(angle) * force,
        life: 0.45 + Math.random() * 0.55,
        maxLife: 0.45 + Math.random() * 0.55,
        size: 1 + Math.random() * (type === 'core' ? 4 : 2.4),
        color: Math.random() > 0.42 ? mint : ice,
        diamond: type !== 'orb' && Math.random() > 0.35
      });
    }
    if (this.particles.length > 130) this.particles.splice(0, this.particles.length - 130);
  };

  GopVault.prototype.updateParticles = function (delta) {
    for (var i = this.particles.length - 1; i >= 0; i -= 1) {
      var particle = this.particles[i];
      particle.life -= delta;
      if (particle.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      particle.x += particle.vx * delta;
      particle.y += particle.vy * delta;
      particle.vx *= 0.985;
      particle.vy = particle.vy * 0.985 + 10 * delta;
    }
    this.shake *= 0.86;
    if (this.shake < 0.08) this.shake = 0;
  };

  GopVault.prototype.setDirectionByName = function (name) {
    if (this.state !== 'playing' || this.paused) return;
    var map = {
      left: { x: -1, y: 0 },
      right: { x: 1, y: 0 },
      up: { x: 0, y: -1 },
      down: { x: 0, y: 1 }
    };
    var next = map[name];
    if (!next) return;
    if (next.x + this.direction.x === 0 && next.y + this.direction.y === 0) return;
    if (next.x === this.pendingDirection.x && next.y === this.pendingDirection.y) return;
    this.pendingDirection = next;
    this.audio.turn();
  };

  GopVault.prototype.togglePause = function (force) {
    if (this.state !== 'playing') return;
    this.paused = typeof force === 'boolean' ? force : !this.paused;
    if (this.pauseScreen) this.pauseScreen.hidden = !this.paused;
    if (!this.paused) this.lastFrame = performance.now();
    this.syncPauseButton();
  };

  GopVault.prototype.syncPauseButton = function () {
    if (!this.pauseButton) return;
    this.pauseButton.classList.toggle('is-paused', this.paused);
    this.pauseButton.setAttribute('aria-label', this.paused ? this.resumeLabel : this.pauseLabel);
  };

  GopVault.prototype.toggleSound = function () {
    this.soundEnabled = !this.soundEnabled;
    this.audio.setEnabled(this.soundEnabled);
    this.syncSoundButton();
    var self = this;
    if (this.soundEnabled) {
      this.audio.unlock().then(function () {
        if (self.state === 'playing' && !self.paused) self.audio.startMusic();
      });
    }
  };

  GopVault.prototype.syncSoundButton = function () {
    if (!this.soundButton) return;
    this.soundButton.classList.toggle('is-on', this.soundEnabled);
    this.soundButton.setAttribute('aria-pressed', String(this.soundEnabled));
  };

  GopVault.prototype.updateHud = function () {
    if (this.scoreElement) this.scoreElement.textContent = pad(this.score, 3);
    if (this.scoreMiniElement) this.scoreMiniElement.textContent = String(this.score);
    if (this.levelElement) this.levelElement.textContent = pad(this.level, 2);
    if (this.progressElement) {
      this.progressElement.style.width = clamp((this.score / this.targetScore) * 100, 0, 100) + '%';
    }
  };

  GopVault.prototype.win = function () {
    if (this.state !== 'playing') return;
    this.state = 'won';
    this.writeBestScore();
    this.audio.win();
    if (navigator.vibrate) navigator.vibrate([30, 50, 35, 50, 70]);
    this.showScreen('win');
    this.stopLoop();
    if (this.liveRegion) this.liveRegion.textContent = this.rewardValue + ' reward unlocked';
  };

  GopVault.prototype.lose = function () {
    if (this.state !== 'playing') return;
    this.state = 'lost';
    this.writeBestScore();
    this.audio.stopMusic();
    this.audio.crash();
    if (navigator.vibrate) navigator.vibrate([45, 35, 70]);
    if (this.finalScoreElement) this.finalScoreElement.textContent = pad(this.score, 3);
    if (this.bestScoreElement) this.bestScoreElement.textContent = pad(this.bestScore, 3);
    this.showScreen('loss');
    this.stopLoop();
  };

  GopVault.prototype.copyReward = function (button) {
    var self = this;
    return copyText(this.discountCode).then(function () {
      button.classList.add('is-copied');
      var label = button.querySelector('small');
      if (label) {
        if (!label.dataset.originalText) label.dataset.originalText = label.textContent;
        label.textContent = self.copiedLabel;
      }
      if (self.liveRegion) self.liveRegion.textContent = self.copiedLabel;
      if (navigator.vibrate) navigator.vibrate(18);
      window.setTimeout(function () {
        button.classList.remove('is-copied');
        if (label && label.dataset.originalText) label.textContent = label.dataset.originalText;
      }, 2200);
    }).catch(function () {
      if (self.liveRegion) self.liveRegion.textContent = self.discountCode;
      return false;
    });
  };

  GopVault.prototype.onKeydown = function (event) {
    if (!this.modalOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
      return;
    }

    if (this.state !== 'playing') return;
    var keyMap = {
      ArrowLeft: 'left',
      a: 'left',
      A: 'left',
      ArrowRight: 'right',
      d: 'right',
      D: 'right',
      ArrowUp: 'up',
      w: 'up',
      W: 'up',
      ArrowDown: 'down',
      s: 'down',
      S: 'down'
    };

    if (keyMap[event.key]) {
      event.preventDefault();
      this.setDirectionByName(keyMap[event.key]);
    } else if (event.key === 'p' || event.key === 'P') {
      event.preventDefault();
      this.togglePause();
    }
  };

  GopVault.prototype.trapFocus = function (event) {
    if (!this.shell) return;
    var focusable = Array.prototype.slice.call(this.shell.querySelectorAll(
      'button:not([disabled]):not([hidden]), [href]:not([hidden]), [tabindex]:not([tabindex="-1"]):not([hidden])'
    )).filter(function (element) {
      return element.offsetParent !== null;
    });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  GopVault.prototype.onVisibilityChange = function () {
    if (document.hidden && this.state === 'playing' && !this.paused) this.togglePause(true);
  };

  GopVault.prototype.getColor = function (variable, fallback) {
    var value = window.getComputedStyle(this.root).getPropertyValue(variable).trim();
    return value || fallback;
  };

  GopVault.prototype.resizeCanvas = function () {
    if (!this.canvas || !this.boardWrap || !this.context || this.boardWrap.hidden) return;
    this.fitBoard();
    var rect = this.boardWrap.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return;
    var dpr = clamp(window.devicePixelRatio || 1, 1, 2);
    var width = Math.round(rect.width);
    var height = Math.round(rect.height);
    var pixelWidth = Math.round(width * dpr);
    var pixelHeight = Math.round(height * dpr);
    if (this.canvas.width !== pixelWidth || this.canvas.height !== pixelHeight) {
      this.canvas.width = pixelWidth;
      this.canvas.height = pixelHeight;
      this.canvas.style.width = width + 'px';
      this.canvas.style.height = height + 'px';
    }
    this.context.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.canvasWidth = width;
    this.canvasHeight = height;
    var padding = Math.max(12, Math.min(width, height) * 0.035);
    this.cell = Math.min((width - padding * 2) / this.cols, (height - padding * 2) / this.rows);
    this.offsetX = (width - this.cell * this.cols) / 2;
    this.offsetY = (height - this.cell * this.rows) / 2;
    if (this.state === 'playing') this.render(performance.now(), 1);
  };

  GopVault.prototype.fitBoard = function () {
    if (!this.gameScreen || this.gameScreen.hidden || !this.boardWrap) return;
    var gameStyle = window.getComputedStyle(this.gameScreen);
    var horizontalPadding = safeNumber(parseFloat(gameStyle.paddingLeft), 0) + safeNumber(parseFloat(gameStyle.paddingRight), 0);
    var verticalPadding = safeNumber(parseFloat(gameStyle.paddingTop), 0) + safeNumber(parseFloat(gameStyle.paddingBottom), 0);
    var hudHeight = this.hud ? this.hud.offsetHeight : 48;
    var footerHeight = this.gameFooter ? this.gameFooter.offsetHeight : 76;
    var availableWidth = Math.max(180, this.gameScreen.clientWidth - horizontalPadding);
    var availableHeight = Math.max(240, this.gameScreen.clientHeight - verticalPadding - hudHeight - footerHeight - 10);
    var boardWidth = Math.floor(Math.min(availableWidth, availableHeight * 0.75));
    var boardHeight = Math.floor(boardWidth / 0.75);
    this.boardWrap.style.width = boardWidth + 'px';
    this.boardWrap.style.height = boardHeight + 'px';
  };

  GopVault.prototype.gridToCanvas = function (gridX, gridY) {
    return {
      x: this.offsetX + (gridX + 0.5) * this.cell,
      y: this.offsetY + (gridY + 0.5) * this.cell
    };
  };

  GopVault.prototype.render = function (timestamp, interpolation) {
    var ctx = this.context;
    if (!ctx || !this.canvasWidth || !this.canvasHeight) return;

    var width = this.canvasWidth;
    var height = this.canvasHeight;
    var mint = this.getColor('--gvg-mint', '#79ffa7');
    var ice = this.getColor('--gvg-ice', '#bdf7ff');
    ctx.save();
    ctx.clearRect(0, 0, width, height);

    var background = ctx.createLinearGradient(0, 0, width, height);
    background.addColorStop(0, '#08100b');
    background.addColorStop(0.48, '#020503');
    background.addColorStop(1, '#07100a');
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);

    if (this.shake && !this.reducedMotion) {
      ctx.translate((Math.random() - 0.5) * this.shake, (Math.random() - 0.5) * this.shake);
    }

    this.drawAmbient(ctx, timestamp, mint, ice);
    this.drawGrid(ctx, timestamp, mint);
    this.drawPickup(ctx, timestamp, mint, ice);
    this.drawSnake(ctx, interpolation, mint, ice);
    this.drawParticles(ctx);

    var vignette = ctx.createRadialGradient(width / 2, height / 2, Math.min(width, height) * 0.18, width / 2, height / 2, Math.max(width, height) * 0.72);
    vignette.addColorStop(0, 'rgba(0,0,0,0)');
    vignette.addColorStop(1, 'rgba(0,0,0,0.42)');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  };

  GopVault.prototype.drawAmbient = function (ctx, timestamp, mint, ice) {
    var time = timestamp * 0.001;
    for (var i = 0; i < this.ambient.length; i += 1) {
      var dot = this.ambient[i];
      var pulse = 0.5 + Math.sin(time * dot.drift + dot.phase) * 0.5;
      ctx.globalAlpha = dot.alpha * (0.5 + pulse * 0.5);
      ctx.fillStyle = i % 4 === 0 ? ice : mint;
      ctx.beginPath();
      ctx.arc(dot.x * this.canvasWidth, dot.y * this.canvasHeight, dot.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  };

  GopVault.prototype.drawGrid = function (ctx, timestamp, mint) {
    var left = this.offsetX;
    var top = this.offsetY;
    var gridWidth = this.cell * this.cols;
    var gridHeight = this.cell * this.rows;

    ctx.save();
    ctx.strokeStyle = 'rgba(121,255,167,0.055)';
    ctx.lineWidth = 1;
    for (var x = 0; x <= this.cols; x += 1) {
      var px = left + x * this.cell;
      ctx.beginPath();
      ctx.moveTo(px, top);
      ctx.lineTo(px, top + gridHeight);
      ctx.stroke();
    }
    for (var y = 0; y <= this.rows; y += 1) {
      var py = top + y * this.cell;
      ctx.beginPath();
      ctx.moveTo(left, py);
      ctx.lineTo(left + gridWidth, py);
      ctx.stroke();
    }

    var sweepY = top + ((timestamp * 0.018) % gridHeight);
    var sweep = ctx.createLinearGradient(0, sweepY - this.cell * 2, 0, sweepY + this.cell * 2);
    sweep.addColorStop(0, 'rgba(121,255,167,0)');
    sweep.addColorStop(0.5, 'rgba(121,255,167,0.055)');
    sweep.addColorStop(1, 'rgba(121,255,167,0)');
    ctx.fillStyle = sweep;
    ctx.fillRect(left, sweepY - this.cell * 2, gridWidth, this.cell * 4);

    roundedRectPath(ctx, left, top, gridWidth, gridHeight, Math.max(8, this.cell * 0.65));
    ctx.strokeStyle = 'rgba(189,247,255,0.16)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  };

  GopVault.prototype.drawPickup = function (ctx, timestamp, mint, ice) {
    var item = this.pickupItem;
    if (!item) return;
    var center = this.gridToCanvas(item.x, item.y);
    var pulse = 0.5 + Math.sin(timestamp * 0.006) * 0.5;
    var base = this.cell * (item.type === 'core' ? 0.42 : item.type === 'shard' ? 0.34 : 0.25);

    ctx.save();
    ctx.translate(center.x, center.y);

    var halo = ctx.createRadialGradient(0, 0, 0, 0, 0, this.cell * 1.45);
    halo.addColorStop(0, item.type === 'core' ? 'rgba(121,255,167,0.42)' : 'rgba(189,247,255,0.28)');
    halo.addColorStop(0.35, item.type === 'core' ? 'rgba(121,255,167,0.12)' : 'rgba(189,247,255,0.07)');
    halo.addColorStop(1, 'rgba(121,255,167,0)');
    ctx.fillStyle = halo;
    ctx.fillRect(-this.cell * 1.5, -this.cell * 1.5, this.cell * 3, this.cell * 3);

    ctx.strokeStyle = item.type === 'core' ? mint : ice;
    ctx.globalAlpha = 0.28 + pulse * 0.35;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(0, 0, base * (1.8 + pulse * 0.25), 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;

    if (item.type === 'orb') {
      var orb = ctx.createRadialGradient(-base * 0.35, -base * 0.35, 0, 0, 0, base);
      orb.addColorStop(0, '#ffffff');
      orb.addColorStop(0.28, ice);
      orb.addColorStop(1, mint);
      ctx.shadowColor = ice;
      ctx.shadowBlur = 12 + pulse * 8;
      ctx.fillStyle = orb;
      ctx.beginPath();
      ctx.arc(0, 0, base * (0.82 + pulse * 0.1), 0, Math.PI * 2);
      ctx.fill();
    } else if (item.type === 'shard') {
      ctx.rotate(timestamp * 0.0012);
      ctx.shadowColor = mint;
      ctx.shadowBlur = 15 + pulse * 8;
      var shard = ctx.createLinearGradient(-base, -base, base, base);
      shard.addColorStop(0, '#ffffff');
      shard.addColorStop(0.32, ice);
      shard.addColorStop(1, mint);
      ctx.fillStyle = shard;
      ctx.beginPath();
      ctx.moveTo(0, -base * 1.25);
      ctx.lineTo(base * 0.86, 0);
      ctx.lineTo(0, base * 1.25);
      ctx.lineTo(-base * 0.86, 0);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.72)';
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, -base * 1.25);
      ctx.lineTo(0, base * 1.25);
      ctx.moveTo(-base * 0.86, 0);
      ctx.lineTo(base * 0.86, 0);
      ctx.strokeStyle = 'rgba(4,25,12,0.28)';
      ctx.stroke();
    } else {
      ctx.rotate(Math.sin(timestamp * 0.0017) * 0.08);
      roundedRectPath(ctx, -base * 1.1, -base * 1.1, base * 2.2, base * 2.2, base * 0.48);
      var core = ctx.createLinearGradient(-base, -base, base, base);
      core.addColorStop(0, 'rgba(255,255,255,0.94)');
      core.addColorStop(0.16, ice);
      core.addColorStop(0.48, mint);
      core.addColorStop(1, '#1d7f41');
      ctx.fillStyle = core;
      ctx.shadowColor = mint;
      ctx.shadowBlur = 22 + pulse * 12;
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.strokeStyle = 'rgba(255,255,255,0.68)';
      ctx.stroke();
      this.drawGMark(ctx, 0, 0, base * 1.5, '#061009');
    }

    ctx.restore();
  };

  GopVault.prototype.drawSnake = function (ctx, interpolation, mint, ice) {
    if (!this.snake.length) return;
    for (var i = this.snake.length - 1; i >= 1; i -= 1) {
      var part = this.snake[i];
      var previous = this.previousSnake[i] || this.previousSnake[this.previousSnake.length - 1] || part;
      var x = previous.x + (part.x - previous.x) * interpolation;
      var y = previous.y + (part.y - previous.y) * interpolation;
      this.drawBodySegment(ctx, x, y, i, mint, ice);
    }

    var head = this.snake[0];
    var previousHead = this.previousSnake[0] || head;
    var headX = previousHead.x + (head.x - previousHead.x) * interpolation;
    var headY = previousHead.y + (head.y - previousHead.y) * interpolation;
    this.drawHead(ctx, headX, headY, mint, ice);
  };

  GopVault.prototype.drawBodySegment = function (ctx, gridX, gridY, index, mint, ice) {
    var center = this.gridToCanvas(gridX, gridY);
    var size = this.cell * clamp(0.78 - index * 0.004, 0.62, 0.78);
    ctx.save();
    ctx.translate(center.x, center.y);
    ctx.rotate(index % 2 === 0 ? 0.025 : -0.025);

    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = this.cell * 0.28;
    ctx.shadowOffsetY = this.cell * 0.12;
    roundedRectPath(ctx, -size / 2, -size / 2, size, size, size * 0.28);
    var gradient = ctx.createLinearGradient(-size / 2, -size / 2, size / 2, size / 2);
    gradient.addColorStop(0, '#27332b');
    gradient.addColorStop(0.33, '#111914');
    gradient.addColorStop(1, '#050906');
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    ctx.strokeStyle = index % 3 === 0 ? 'rgba(189,247,255,0.28)' : 'rgba(121,255,167,0.27)';
    ctx.lineWidth = Math.max(0.8, this.cell * 0.045);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(-size * 0.31, -size * 0.25);
    ctx.lineTo(0, -size * 0.4);
    ctx.lineTo(size * 0.31, -size * 0.25);
    ctx.lineTo(size * 0.19, 0);
    ctx.lineTo(-size * 0.19, 0);
    ctx.closePath();
    ctx.fillStyle = 'rgba(255,255,255,0.055)';
    ctx.fill();

    ctx.fillStyle = mint;
    ctx.shadowColor = mint;
    ctx.shadowBlur = 8;
    roundedRectPath(ctx, -size * 0.28, size * 0.43, size * 0.56, Math.max(1.2, size * 0.055), 99);
    ctx.fill();
    ctx.restore();
  };

  GopVault.prototype.drawHead = function (ctx, gridX, gridY, mint, ice) {
    var center = this.gridToCanvas(gridX, gridY);
    var size = this.cell * 1.34;
    var angle = Math.atan2(this.direction.y, this.direction.x);
    ctx.save();
    ctx.translate(center.x, center.y);
    ctx.rotate(angle);

    ctx.shadowColor = 'rgba(121,255,167,0.22)';
    ctx.shadowBlur = this.cell * 0.62;
    ctx.beginPath();
    ctx.moveTo(size * 0.56, 0);
    ctx.lineTo(size * 0.25, -size * 0.42);
    ctx.lineTo(-size * 0.22, -size * 0.5);
    ctx.lineTo(-size * 0.5, -size * 0.24);
    ctx.lineTo(-size * 0.42, 0);
    ctx.lineTo(-size * 0.5, size * 0.24);
    ctx.lineTo(-size * 0.22, size * 0.5);
    ctx.lineTo(size * 0.25, size * 0.42);
    ctx.closePath();
    var headGradient = ctx.createLinearGradient(-size * 0.4, -size * 0.4, size * 0.5, size * 0.4);
    headGradient.addColorStop(0, '#26342b');
    headGradient.addColorStop(0.45, '#101914');
    headGradient.addColorStop(1, '#050906');
    ctx.fillStyle = headGradient;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = 'rgba(121,255,167,0.5)';
    ctx.lineWidth = Math.max(1, this.cell * 0.055);
    ctx.stroke();

    /* Horns */
    ctx.fillStyle = '#d9fff0';
    ctx.beginPath();
    ctx.moveTo(-size * 0.23, -size * 0.38);
    ctx.lineTo(-size * 0.48, -size * 0.69);
    ctx.lineTo(-size * 0.38, -size * 0.29);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(-size * 0.23, size * 0.38);
    ctx.lineTo(-size * 0.48, size * 0.69);
    ctx.lineTo(-size * 0.38, size * 0.29);
    ctx.closePath();
    ctx.fill();

    /* Face facets */
    ctx.beginPath();
    ctx.moveTo(size * 0.48, 0);
    ctx.lineTo(size * 0.08, -size * 0.16);
    ctx.lineTo(-size * 0.14, 0);
    ctx.lineTo(size * 0.08, size * 0.16);
    ctx.closePath();
    ctx.fillStyle = 'rgba(255,255,255,0.065)';
    ctx.fill();

    /* Eyes */
    var eyeX = size * 0.16;
    var eyeY = size * 0.21;
    ctx.shadowColor = mint;
    ctx.shadowBlur = this.cell * 0.35;
    ctx.fillStyle = mint;
    ctx.beginPath();
    ctx.moveTo(eyeX - size * 0.09, -eyeY - size * 0.035);
    ctx.lineTo(eyeX + size * 0.1, -eyeY);
    ctx.lineTo(eyeX - size * 0.03, -eyeY + size * 0.07);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(eyeX - size * 0.09, eyeY + size * 0.035);
    ctx.lineTo(eyeX + size * 0.1, eyeY);
    ctx.lineTo(eyeX - size * 0.03, eyeY - size * 0.07);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;

    /* Fangs */
    ctx.fillStyle = '#f3fff7';
    ctx.beginPath();
    ctx.moveTo(size * 0.39, -size * 0.12);
    ctx.lineTo(size * 0.55, -size * 0.08);
    ctx.lineTo(size * 0.4, -size * 0.015);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(size * 0.39, size * 0.12);
    ctx.lineTo(size * 0.55, size * 0.08);
    ctx.lineTo(size * 0.4, size * 0.015);
    ctx.closePath();
    ctx.fill();

    /* Brand mark on forehead, rotated back so it remains readable. */
    ctx.save();
    ctx.translate(-size * 0.17, 0);
    ctx.rotate(-angle);
    this.drawGMark(ctx, 0, 0, size * 0.38, mint);
    ctx.restore();
    ctx.restore();
  };

  GopVault.prototype.drawGMark = function (ctx, x, y, size, color) {
    if (this.markImage && this.markImage.complete) {
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = size * 0.2;
      ctx.drawImage(this.markImage, x - size / 2, y - size / 2, size, size);
      ctx.restore();
      return;
    }

    ctx.save();
    ctx.translate(x, y);
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1.4, size * 0.13);
    ctx.lineCap = 'square';
    ctx.lineJoin = 'miter';
    ctx.shadowColor = color;
    ctx.shadowBlur = size * 0.22;
    ctx.beginPath();
    ctx.moveTo(size * 0.34, -size * 0.32);
    ctx.lineTo(-size * 0.12, -size * 0.32);
    ctx.lineTo(-size * 0.39, 0);
    ctx.lineTo(-size * 0.1, size * 0.34);
    ctx.lineTo(size * 0.34, size * 0.34);
    ctx.lineTo(size * 0.34, 0.02 * size);
    ctx.lineTo(0.02 * size, 0.02 * size);
    ctx.lineTo(-size * 0.1, size * 0.16);
    ctx.stroke();
    ctx.restore();
  };

  GopVault.prototype.drawParticles = function (ctx) {
    for (var i = 0; i < this.particles.length; i += 1) {
      var particle = this.particles[i];
      var alpha = clamp(particle.life / particle.maxLife, 0, 1);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = particle.color;
      ctx.shadowColor = particle.color;
      ctx.shadowBlur = particle.size * 3;
      ctx.translate(particle.x, particle.y);
      if (particle.diamond) {
        ctx.rotate(Math.PI / 4);
        ctx.fillRect(-particle.size, -particle.size, particle.size * 2, particle.size * 2);
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, particle.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  };

  GopVault.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;
    this.stopLoop();
    this.audio.destroy();
    document.removeEventListener('keydown', this.boundKeydown);
    document.removeEventListener('visibilitychange', this.boundVisibility);
    window.removeEventListener('resize', this.boundResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.modalOpen) document.body.classList.remove('gvg-no-scroll');
    this.restoreModal();
    delete this.root.__gopVaultInstance;
    delete this.root.dataset.gvgReady;
  };

  function init(scope) {
    var container = scope || document;
    var roots = [];
    if (container.matches && container.matches(ROOT_SELECTOR)) roots.push(container);
    if (container.querySelectorAll) {
      roots = roots.concat(Array.prototype.slice.call(container.querySelectorAll(ROOT_SELECTOR)));
    }
    roots.forEach(function (root) {
      if (root.__gopVaultInstance) return;
      root.__gopVaultInstance = new GopVault(root);
    });
  }

  function boot() {
    init(document);
    document.addEventListener('shopify:section:load', function (event) { init(event.target); });
    document.addEventListener('shopify:section:unload', function (event) {
      var root = event.target.matches && event.target.matches(ROOT_SELECTOR)
        ? event.target
        : event.target.querySelector && event.target.querySelector(ROOT_SELECTOR);
      if (root && root.__gopVaultInstance) root.__gopVaultInstance.destroy();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}());